import { useCallback } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { serverErrorMessage } from "../lib/apiError";
import { useCachedQuery } from "./useCachedQuery";

// Mirrors SecureNoteView on the backend. Deliberately no value field:
// secure notes are write-only for the owner — the value can never be
// fetched back through any user-facing endpoint.
export type SecureNote = {
  id: string;
  name: string;
  createdAt: string;
  expiresAt: string;
};

export const SECURE_NOTE_EXPIRY_OPTIONS = [7, 3, 1] as const;
export type SecureNoteExpiryDays = (typeof SECURE_NOTE_EXPIRY_OPTIONS)[number];
// UTF-8 BYTES, matching the server's SSM-derived cap — not characters.
export const SECURE_NOTE_MAX_VALUE_BYTES = 4000;

export type SecureNotesStatus = "loading" | "ready" | "error";

export type SecureNotesResult<T> =
  | { ok: true; value: T }
  | { ok: false; message: string };

export type UseSecureNotesResult = {
  status: SecureNotesStatus;
  notes: ReadonlyArray<SecureNote>;
  errorMessage: string | null;
  refetch: () => Promise<void>;
  createNote: (
    name: string,
    value: string,
    expiryDays: SecureNoteExpiryDays,
  ) => Promise<SecureNotesResult<SecureNote>>;
  deleteNote: (id: string) => Promise<SecureNotesResult<void>>;
};

const LIST_URL = "/api/secure-notes";

const fetchNotes = async (signal: AbortSignal): Promise<ReadonlyArray<SecureNote>> => {
  const res = await fetch(LIST_URL, { credentials: "include", signal });
  if (!res.ok) throw new Error(`Failed to load secure notes (${res.status})`);
  return (await res.json()) as ReadonlyArray<SecureNote>;
};

export const useSecureNotes = (): UseSecureNotesResult => {
  const { status, data, error, refetch, mutate } = useCachedQuery<ReadonlyArray<SecureNote>>({
    cacheResource: "secure-notes:self",
    queryKey: LIST_URL,
    fetcher: fetchNotes,
  });
  const notes = data ?? [];

  const createNote = useCallback(
    async (
      name: string,
      value: string,
      expiryDays: SecureNoteExpiryDays,
    ): Promise<SecureNotesResult<SecureNote>> => {
      try {
        const res = await fetch(LIST_URL, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ name, value, expiryDays }),
        });
        if (res.status === 201) {
          const note = (await res.json()) as SecureNote;
          // Write-through: server orders created_at DESC, so prepending
          // matches; no follow-up GET to block the modal close on.
          mutate((prev) => [note, ...prev]);
          return { ok: true, value: note };
        }
        return { ok: false, message: await serverErrorMessage(res, "Create failed") };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  const deleteNote = useCallback(
    async (id: string): Promise<SecureNotesResult<void>> => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(id)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.status === 204) {
          mutate((prev) => prev.filter((n) => n.id !== id));
          return { ok: true, value: undefined };
        }
        return { ok: false, message: await serverErrorMessage(res, "Delete failed") };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  return { status, notes, errorMessage: error, refetch, createNote, deleteNote };
};
